/**
 * personaGroupLogic — pure functions behind the Persona Groups feature.
 *
 * Everything here is deliberately framework-free (no React, no Apollo) so it
 * can be unit-tested without mounting a component or driving @dnd-kit through
 * the DOM — same rationale as computeDefaultTabUpserts in PersonasPage.tsx.
 *
 * FLOOR SEMANTICS (the inheritance model — see the reviewed prototype):
 *   visibility : union(group modules, own modules)
 *   actions    : union(group actions, own actions) — the group's are locked
 *   sentinels  : own only, NEVER inherited (defaultTab/collapsedByDefault are
 *                presentation, not permissions, and defaultTab is exclusive
 *                per-persona — copying it from a group would break that)
 *   dataScope  : the group's restriction is a locked baseline a member may
 *                only narrow further, never relax (requireIpaMatch is OR'd,
 *                allowedLineOfBusiness is intersected when both sides restrict
 *                it) — inverting under a floor would let a member escape the
 *                group's PHI boundary
 *   sortOrder  : per-holder, never inherited (presentation, not privilege)
 */
import type { AcAdminPersonaModule, AcDataScope, UpsertPersonaModuleVars } from "../graphql/adminOps";

// ---------------------------------------------------------------------------
// Sentinels — mirrors PersonasPage.tsx's DEFAULT_TAB_ACTION /
// COLLAPSED_BY_DEFAULT_ACTION verbatim (that file re-exports these two so its
// existing test imports keep working). Kept as literals here (rather than an
// import from PersonasPage.tsx) to avoid a page → lib → page import cycle;
// the two files are unit-tested together (PersonasPage.defaultTab.test.ts)
// so any future drift would fail loudly.
// ---------------------------------------------------------------------------
export const DEFAULT_TAB_ACTION = "defaultTab";
export const COLLAPSED_BY_DEFAULT_ACTION = "collapsedByDefault";

export function isSentinelAction(action: string): boolean {
  return action === DEFAULT_TAB_ACTION || action === COLLAPSED_BY_DEFAULT_ACTION;
}

// ---------------------------------------------------------------------------
// MODULE_GROUPS (ACT-1309) — a parent module (e.g. "Home") and its matching
// sub-modules (e.g. HomeHcc, HomeDemographics, ...) render as a single
// collapsible group in the matrix so the module list stays scannable.
// Grouping only kicks in when the group's parent module actually exists on
// the holder (persona or group); orphaned children fall back to plain
// top-level rows. Shared between ModuleMatrix.tsx (rendering) and the
// default-tab helpers below (a Home* card is never a settable landing tab).
// ---------------------------------------------------------------------------
export interface ModuleGroupConfig {
  parentId: string;
  matchChild: (moduleId: string) => boolean;
}
export const MODULE_GROUPS: ModuleGroupConfig[] = [
  { parentId: "Home", matchChild: (id) => id !== "Home" && id.startsWith("Home") },
];

// ---------------------------------------------------------------------------
// Default (landing) tab — the module a user lands on when opening a member.
// Encoded as the DEFAULT_TAB_ACTION sentinel in a module's `actions[]`, own
// only, at most ONE holder per persona (enforced by computeDefaultTabUpserts
// below). Sentinels are never inherited, so this is persona-scope-only.
// ---------------------------------------------------------------------------

// A module is SETTABLE as a persona's default tab only if it's a real,
// independently-navigable TAB — not a Home* card (those render inside the
// collapsible Home group and are never a landing target) and not an
// inactive/deactivated module. "Home" itself IS settable: it fails every
// group's matchChild (which excludes its own parentId).
export function isDefaultTabSettable(moduleId: string, isActive: boolean): boolean {
  if (!isActive) return false;
  return !MODULE_GROUPS.some((g) => g.matchChild(moduleId));
}

export interface DefaultTabRowState {
  isDefault: boolean;
  stale: boolean;
}

// The default-tab control's render decision for one row, as a pure function.
// `holdsSentinel` = does this row currently carry DEFAULT_TAB_ACTION. Returns
// undefined when the control should be hidden entirely (not settable AND
// doesn't hold the sentinel); otherwise `stale` marks the clear-only
// data-repair case (holds it but isn't settable — deactivated, or an orphaned
// Home* card) versus the normal selectable case.
export function resolveDefaultTabRowState(
  moduleId: string,
  isActive: boolean,
  holdsSentinel: boolean,
): DefaultTabRowState | undefined {
  const settable = isDefaultTabSettable(moduleId, isActive);
  if (!holdsSentinel && !settable) return undefined;
  return { isDefault: holdsSentinel, stale: holdsSentinel && !settable };
}

// resolveDefaultTabControl — the Persona-Groups-aware wrapper ModuleMatrix.tsx
// uses. A WHOLLY-INHERITED row (fromGroup && no ownModule) can never hold
// DEFAULT_TAB_ACTION — sentinels are own-only — so its control must be hidden
// entirely, not just disabled. Fixing this at the row level (rather than
// trusting the caller to remember to gate it) is what closes the bug where a
// wholly-inherited row rendered a clickable default-tab toggle: clicking it
// called computeDefaultTabUpserts, which only walks the persona's OWN
// modules, so it produced an upsert CLEARING whichever module actually held
// the sentinel but never one SETTING it on the (ownerless) clicked row — the
// admin's default tab silently vanished with no error.
export function resolveDefaultTabControl(
  row: Pick<ResolvedModuleRow, "moduleId" | "isActive" | "fromGroup" | "ownModule" | "sentinels">,
): DefaultTabRowState | undefined {
  const whollyInherited = row.fromGroup && !row.ownModule;
  if (whollyInherited) return undefined;
  const holdsSentinel = row.sentinels.includes(DEFAULT_TAB_ACTION);
  return resolveDefaultTabRowState(row.moduleId, row.isActive, holdsSentinel);
}

// computeDefaultTabUpserts — the exclusivity computation. Given a persona's
// current modules and the moduleId that should hold DEFAULT_TAB_ACTION after
// this change (null = no module should hold it), returns the upsert variable
// objects needed to get there: one per module whose sentinel presence doesn't
// already match the target (so calling this with the already-correct target
// is a no-op — returns []). Each upsert re-sends that module's OWN current
// sortOrder and dataScope, and preserves its other actions untouched —
// sortOrder is Int! and ALWAYS overwrites server-side, and actions
// omitted/undefined COALESCE-preserves the stale value, so guessing either
// would silently corrupt state (see UPSERT_PERSONA_MODULE_MUTATION's
// contract in adminOps.ts).
export function computeDefaultTabUpserts(
  personaCode: string,
  modules: AcAdminPersonaModule[],
  targetModuleId: string | null,
): UpsertPersonaModuleVars[] {
  const upserts: UpsertPersonaModuleVars[] = [];
  for (const mod of modules) {
    const current = mod.actions ?? [];
    const hasFlag = current.includes(DEFAULT_TAB_ACTION);
    const shouldHaveFlag = mod.moduleId === targetModuleId;
    if (hasFlag === shouldHaveFlag) continue; // already correct — nothing to persist

    const nextActions = shouldHaveFlag
      ? [...current, DEFAULT_TAB_ACTION]
      : current.filter((a) => a !== DEFAULT_TAB_ACTION);

    upserts.push({
      personaCode,
      moduleId: mod.moduleId,
      sortOrder: mod.sortOrder,
      actions: nextActions,
      dataScope: serializeScope(mod.dataScope),
      isActive: mod.isActive,
    });
  }
  return upserts;
}

function hasScopeContent(scope: AcDataScope | null | undefined): boolean {
  return !!scope && (!!scope.requireIpaMatch || (scope.allowedLineOfBusiness?.length ?? 0) > 0);
}

function serializeScope(scope: AcDataScope | null | undefined): string | undefined {
  if (!hasScopeContent(scope)) return undefined;
  return JSON.stringify({
    requireIpaMatch: scope!.requireIpaMatch ?? false,
    ...(scope!.allowedLineOfBusiness?.length ? { allowedLineOfBusiness: scope!.allowedLineOfBusiness } : {}),
  });
}

// ---------------------------------------------------------------------------
// resolveEffectiveModules — the inheritance resolver.
// ---------------------------------------------------------------------------
export interface ResolvedModuleRow {
  moduleId: string;
  /** Action verbs granted by the group (sentinels excluded — never inherited). */
  inherited: string[];
  /** Action verbs granted directly, minus anything already inherited. */
  own: string[];
  /** Presentation sentinels held directly (own only). */
  sentinels: string[];
  groupScope: AcDataScope | null;
  ownScope: AcDataScope | null;
  /** true when this module has no own row at all — wholly inherited. */
  fromGroup: boolean;
  isActive: boolean;
  /** The holder's own raw module row, if one exists (carries sortOrder etc). */
  ownModule: AcAdminPersonaModule | null;
}

export function resolveEffectiveModules(
  ownModules: AcAdminPersonaModule[],
  groupModules?: AcAdminPersonaModule[] | null,
): ResolvedModuleRow[] {
  const out = new Map<string, ResolvedModuleRow>();
  if (groupModules) {
    for (const gm of groupModules) {
      out.set(gm.moduleId, {
        moduleId: gm.moduleId,
        inherited: (gm.actions ?? []).filter((a) => !isSentinelAction(a)),
        own: [],
        sentinels: [],
        groupScope: gm.dataScope,
        ownScope: null,
        fromGroup: true,
        isActive: gm.isActive,
        ownModule: null,
      });
    }
  }
  for (const pm of ownModules) {
    const existing = out.get(pm.moduleId);
    const inherited = existing?.inherited ?? [];
    const actions = pm.actions ?? [];
    // isActive is a UNION, not an override: a persona cannot deactivate what
    // the group actively grants (floor semantics — "visibility: union"). If
    // the own row overwrote this outright, deactivating a persona's own row
    // for a module the group also grants would incorrectly hide it entirely.
    const groupGrantsActive = (existing?.fromGroup ? existing.isActive : false) ?? false;
    out.set(pm.moduleId, {
      moduleId: pm.moduleId,
      inherited,
      own: actions.filter((a) => !isSentinelAction(a) && !inherited.includes(a)),
      sentinels: actions.filter(isSentinelAction),
      groupScope: existing?.groupScope ?? null,
      ownScope: pm.dataScope,
      fromGroup: false,
      isActive: groupGrantsActive || pm.isActive,
      ownModule: pm,
    });
  }
  return Array.from(out.values());
}

/** Sort resolved rows by a canonical module order (e.g. FEATURE_CATALOG's key order); unknown ids sort last, stable otherwise. */
export function orderResolvedRows(rows: ResolvedModuleRow[], order: readonly string[]): ResolvedModuleRow[] {
  const index = (id: string) => {
    const i = order.indexOf(id);
    return i === -1 ? order.length : i;
  };
  return [...rows].sort((a, b) => index(a.moduleId) - index(b.moduleId));
}

export interface EffectiveModuleCounts {
  inheritedModules: number;
  ownModules: number;
  ownActions: number;
  /** ownModules + ownActions — the "N additions on top of the group" summary number. */
  additions: number;
}

export function computeEffectiveCounts(rows: ResolvedModuleRow[]): EffectiveModuleCounts {
  let inheritedModules = 0;
  let ownModules = 0;
  let ownActions = 0;
  for (const r of rows) {
    if (r.fromGroup) inheritedModules++;
    else ownModules++;
    ownActions += r.own.length;
  }
  return { inheritedModules, ownModules, ownActions, additions: ownModules + ownActions };
}

// ---------------------------------------------------------------------------
// commonGrants — what a new group formed from a set of personas could lift.
// Modules+actions shared by EVERY persona in the list. Sentinels are never
// eligible (never lifted into a group).
// ---------------------------------------------------------------------------
export interface CommonGrant {
  moduleId: string;
  actions: string[];
}

export function computeCommonGrants(
  personas: { code: string; modules: AcAdminPersonaModule[] }[],
): CommonGrant[] {
  if (personas.length === 0) return [];
  const [first, ...rest] = personas;
  const shared: CommonGrant[] = [];
  for (const mod of first.modules) {
    const others = rest.map((p) => p.modules.find((x) => x.moduleId === mod.moduleId));
    if (others.some((o) => !o)) continue; // not every persona has this module
    const ownActions = (mod.actions ?? []).filter((a) => !isSentinelAction(a));
    const verbs = ownActions.filter((a) => others.every((o) => (o!.actions ?? []).includes(a)));
    shared.push({ moduleId: mod.moduleId, actions: verbs });
  }
  return shared;
}

/** Total shared action-verb grants across a commonGrants() result (for dialog copy). */
export function countSharedActions(shared: CommonGrant[]): number {
  return shared.reduce((n, s) => n + s.actions.length, 0);
}

// ---------------------------------------------------------------------------
// computeStripLiftedGrants — "Create group from selection" promises to
// REMOVE the lifted grants from each persona's own rows (the group now
// covers them, so keeping duplicate own copies would mean a later edit to
// the group never reaches that persona — it'd count as "keeping it, granted
// directly" and the group becomes decorative). This computes the upserts
// that make the promise true, applied AFTER the group + memberships are
// created (never before — a partial failure here should leave a DUPLICATE
// grant, not a missing one; erring toward too much access is recoverable,
// a lockout mid-shift is not).
//
// Sentinels are NEVER stripped (they were never lifted in the first place —
// computeCommonGrants already excludes them). A module is never deactivated
// here — only emptied of the now-redundant actions — because deactivating a
// persona's own row is only safe to do once the merged isActive is properly
// OR'd with the group's (see resolveEffectiveModules): this function doesn't
// assume that invariant holds everywhere it might be reused, so it sticks to
// the always-safe operation (empty the actions, leave isActive untouched).
// ---------------------------------------------------------------------------
export interface StripGrantUpsert {
  personaCode: string;
  moduleId: string;
  sortOrder: number;
  actions: string[];
  dataScope?: string;
  isActive: boolean;
}

export function computeStripLiftedGrants(
  personaCode: string,
  personaModules: AcAdminPersonaModule[],
  lifted: CommonGrant[],
): StripGrantUpsert[] {
  const upserts: StripGrantUpsert[] = [];
  for (const grant of lifted) {
    const own = personaModules.find((m) => m.moduleId === grant.moduleId);
    if (!own) continue; // nothing to strip
    // A persona's own dataScope may be a genuine, STRICTER restriction of its
    // own (e.g. narrower allowedLineOfBusiness) that computeCommonGrants never
    // considered when computing what to lift (the group's lifted module gets
    // NO dataScope — only actions are lifted). Stripping/deactivating this row
    // could discard that restriction and RELAX access — the one direction
    // floor semantics must never allow. So if the own row carries any scope
    // content, leave it fully alone (duplicate module presence is the safe
    // failure mode here, not a narrower one silently vanishing).
    if (hasScopeContent(own.dataScope)) continue;

    const currentActions = own.actions ?? [];
    // Keep sentinels always; drop exactly the lifted verbs; keep anything
    // else the persona had beyond what got lifted.
    const remaining = currentActions.filter((a) => isSentinelAction(a) || !grant.actions.includes(a));
    // An all-or-nothing module (grant carries no actions at all) whose own
    // row ALSO has no actions is pure duplicate presence — the group now
    // guarantees it, so the own row can safely be deactivated (never
    // deleted — no such mutation exists). Safe specifically because
    // resolveEffectiveModules ORs the group's active grant into the merged
    // isActive, so deactivating here can never hide the module from the
    // persona.
    const isAllOrNothingDuplicate = grant.actions.length === 0 && currentActions.length === 0;
    if (remaining.length === currentActions.length && !isAllOrNothingDuplicate) continue; // nothing this lift actually narrows
    upserts.push({
      personaCode,
      moduleId: own.moduleId,
      sortOrder: own.sortOrder,
      actions: remaining,
      dataScope: undefined, // already verified above to have no content
      isActive: isAllOrNothingDuplicate ? false : own.isActive,
    });
  }
  return upserts;
}

// ---------------------------------------------------------------------------
// NOTE — there is deliberately NO client-side "materialize group grants onto
// a persona" helper here. Leaving a group (ungrouping, or "keep the
// personas" on delete) must be non-destructive — nothing the persona could
// do a moment ago should stop working — and that materialization is done
// SERVER-SIDE, inside the same transaction as the ungroup/delete write
// (care-management-gql's `AcAdminRepository.materializeGroupGrants`, verified
// against a real Postgres container: actions unioned, the floor's dataScope
// adopted only when the persona had none, the persona's own narrower scope
// preserved, sentinels preserved with no cross-member leak, pre-existing
// sortOrder untouched). Re-doing that client-side here would be redundant
// AND worse — non-atomic and racy against the same write. See MoveDialog's
// Ungrouped case and DeleteGroupDialog's "Keep the personas" case in
// PersonaGroupDialogs.tsx, which rely on the backend for this.
// ---------------------------------------------------------------------------
// Delete-group impact — the numbers the delete dialog states BEFORE the
// admin chooses an outcome (see the prototype's deleteGroupDialog).
// ---------------------------------------------------------------------------
/** Oxford-and joins a name list: "A", "A and B", "A, B and C". */
export function formatNameList(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

// A per-persona user count that hasn't resolved yet (still loading, or the
// listUsers lookup failed) is neither 0 nor a number — collapsing it to 0
// would let a destructive dialog confidently understate real impact.
export type UserCountState = number | "loading" | "failed";

export interface DeleteGroupImpact {
  moduleCount: number;
  actionGrantCount: number;
  memberCount: number;
  /** null = not fully known yet (at least one member's user count hasn't resolved). */
  userCount: number | null;
  memberNamesList: string;
}

export function summarizeDeleteGroupImpact(
  group: { modules: AcAdminPersonaModule[] },
  members: { displayName: string; userCount: UserCountState }[],
): DeleteGroupImpact {
  const actionGrantCount = group.modules.reduce(
    (n, x) => n + (x.actions ?? []).filter((a) => !isSentinelAction(a)).length,
    0,
  );
  const allKnown = members.every((m) => typeof m.userCount === "number");
  const userCount = allKnown ? members.reduce((n, m) => n + (m.userCount as number), 0) : null;
  return {
    moduleCount: group.modules.length,
    actionGrantCount,
    memberCount: members.length,
    userCount,
    memberNamesList: formatNameList(members.map((m) => m.displayName)),
  };
}

// ---------------------------------------------------------------------------
// Narrowing a group grant — inline confirm impact ("removes X from N
// personas; M keep it because they granted it directly").
// ---------------------------------------------------------------------------
export interface NarrowImpact {
  losing: number;
  keeping: number;
}

/** action = null means "remove the whole module row", not just one verb. */
export function computeNarrowImpact(
  members: { modules: AcAdminPersonaModule[] }[],
  moduleId: string,
  action: string | null,
): NarrowImpact {
  let losing = 0;
  let keeping = 0;
  for (const p of members) {
    const own = p.modules.find((x) => x.moduleId === moduleId);
    const hasOwn = action ? !!own?.actions?.includes(action) : !!own;
    if (hasOwn) keeping++;
    else losing++;
  }
  return { losing, keeping };
}

// ---------------------------------------------------------------------------
// toCode — derive a stable, API-safe group code from a display name
// (New group dialog: "Care Team" -> "CARE_TEAM").
// ---------------------------------------------------------------------------
export function toCode(input: string): string {
  return input
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
}
